import {Injectable} from '@angular/core';
import {combineLatest, Observable, of, ReplaySubject} from 'rxjs';
import {DataEntity, OctopusConnectService, OrderDirection, PaginatedCollection} from 'octopus-connect';
import {filter, mergeMap, map, take, tap} from 'rxjs/operators';
import {CollectionOptionsInterface} from 'octopus-connect';
import {clone, cloneDeep, get, groupBy, isEqual, merge, uniq, uniqBy} from 'lodash-es';

import {CommunicationCenterService} from '@modules/communication-center';

/**
 * Used to visualize editable notepad data.
 * Sometimes it's only used for `text` fields, sometimes only the others
 */
export interface INotepadFormData {
    title?: string;
    associatedLessonIds?: (string | number)[];
    text?: string;
}

const granuleEndpoint = 'granule';
const notepadEndpoint = 'notepads';
const searchEndpoint = 'basic_search';

const notepadTypologyLabel = 'notepad';

@Injectable({
    providedIn: 'root'
})
/**
 * Define the exchange between front & backend in matter of notepad
 */
export class NotepadRepositoryService {

    constructor(
        private octopusConnect: OctopusConnectService,
        private communicationCenter: CommunicationCenterService
    ) {
    }

    /**
     * Delete a notepad defined by the granule id. The metadatas, activity & activity_content are deleted on cascade
     * @param id
     */
    public destroyNotepad(id: number | string): Observable<boolean> {
        // Hack: no need to reload the entity, we can mock it :)
        const entity = new DataEntity(granuleEndpoint, {}, this.octopusConnect, id);
        return entity.remove();
    }

    /**
     * Return the notepad {@link DataEntity} as a granule (contains granule, medatada, activity & activityContent)
     * @param id
     */
    public getNotepad(id: string | number): Observable<DataEntity> {
        return this.octopusConnect.loadEntity(granuleEndpoint, id);
    }

    /**
     * Path a notepad entity and return it
     *
     * @param notepadId the id of the notepad
     * @param data the data to path, each data are not required, only the given key will be patched.
     * Some data are in metadata of granule, other in activity_content. This method know what to do.
     * @param returnUpdatedGranule If true or by default, returns the patched notepad granule (but a request is made),
     * otherwise returns the granule in the same state as before patching (and no request are done)
     */
    public updateNotepad(notepadId: string | number, data: INotepadFormData, returnUpdatedGranule = true): Observable<DataEntity> {
        const updateActivityContent = (notepadGranule: DataEntity) =>
            this.getNotepadActivityContent(notepadGranule.get('reference').activity_content[0].id).pipe(
                mergeMap(notepadActivityContent => {
                        if (data.hasOwnProperty('text')) {
                            notepadActivityContent.set('text', data.text);
                        }
                        if (data.hasOwnProperty('associatedLessonIds')) {
                            const lessonIds = data.associatedLessonIds.map(id => +id);
                            const distinctLessonIds = uniq(lessonIds);
                            notepadActivityContent.set('associated_nodes', distinctLessonIds);
                        }
                        return notepadActivityContent.save();
                    }
                ),
                take(1)
            );

        const updateMetadata = (notepadGranule: DataEntity) => {
            const metadata = <DataEntity>notepadGranule.getEmbed('metadatas');
            metadata.set('title', data.title);
            return metadata.save();
        };

        return this.getNotepad(notepadId).pipe(
            mergeMap(notepadGranule => {
                const obs: Observable<DataEntity | any>[] = [of([])];
                if (data.hasOwnProperty('text') || data.hasOwnProperty('associatedLessonIds')) {
                    obs.push(updateActivityContent(notepadGranule));
                }
                if (data.hasOwnProperty('title')) {
                    obs.push(updateMetadata(notepadGranule));
                }

                return combineLatest(obs).pipe(
                    mergeMap(() => returnUpdatedGranule ? this.getNotepad(notepadId) : of(notepadGranule))
                );
            }),
        );
    }

    /**
     * Obtains the paginated list of notes
     * @param filterOptions
     * @return The {@link DataEntity} are `granules` and the are not of `notepads` but `BasicSearch` endpoint
     */
    public getPaginatedNotepads(filterOptions: CollectionOptionsInterface = {}): Observable<PaginatedCollection> {
        return this.getNotepadActivityTypologyId().pipe(
            map(typologyId => {
                filterOptions = merge({
                    filter: {
                        typology: typologyId,
                    },
                    orderOptions: [
                        {field: 'created', direction: OrderDirection.DESC}
                    ]
                }, filterOptions);

                if ('metadatas_title' in filterOptions.filter) {
                    filterOptions.filter.metadatas_title = `%${filterOptions.filter.metadatas_title}%`;
                }

                return this.octopusConnect.paginatedLoadCollection(searchEndpoint, filterOptions);
            }),
            take(1)
        );
    }

    /**
     * Create and obtain a new notepad activity
     *
     * - The activity is create by the generic activity creation of activity module.
     * - This way to create an activity don't set an activity content, that's why an activity_content are created and associated here.
     * - This way to create an activity don't allow to set default values, that's why the empty activity are updated here just after creation.
     *
     * @param notepadData
     */
    public createNotepad(notepadData: INotepadFormData): Observable<DataEntity> {
        const onActivityCreated = new ReplaySubject<Observable<DataEntity>[]>(1);

        this.octopusConnect.createEntity(notepadEndpoint, {title: 'another notepad' /* useless but required by server */})
            .pipe(
                tap(activityContent => {
                    this.communicationCenter
                        .getRoom('activities')
                        .next('createActivities', {
                            types: [{label: notepadTypologyLabel, activity_content: activityContent.id}],
                            callbackSubject: onActivityCreated
                        });
                })
            )
            .subscribe();

        return onActivityCreated.pipe(
            // We can do that because here there is only one notepad to create
            mergeMap(obs => obs[0]),
            mergeMap(notepadGranule => this.updateNotepad(notepadGranule.id, notepadData)),
            take(1)
        );
    }

    /**
     * Obtains the `activity_content` from `notepads` endpoint
     *
     * @param id
     */
    public getNotepadActivityContent(id: string | number): Observable<DataEntity> {
        return this.octopusConnect.loadEntity(notepadEndpoint, id);
    }

    /**
     * Obtain the id of the only one activity typology with `notepad` as label
     */
    private getNotepadActivityTypologyId(): Observable<string> {
        return this.octopusConnect.loadCollection('variables/instance')
            .pipe(
                filter(collection => collection.entities.length > 0),
                map(collection => collection.entities[0].get('activityTypes')),
                map(activityTypes =>
                    activityTypes.filter(activityType =>
                        activityType.label === notepadTypologyLabel
                    ).map(activityType => activityType.id)[0]
                ),
                take(1)
            );
    }
}
